import { QueryStatus } from "@reduxjs/toolkit/dist/query"
import { MutableRefObject, useCallback, useRef, useState } from "react"
import {
  useLazyValidateNMLSQuery,
  useLazyValidateEmailQuery,
  useLazyFetchSubscriptionDetailsQuery,
  useLazyFetchSubscriptionMatchesQuery,
  useLazyFetchSubscriptionsByLeadIdQuery
} from "../../redux/queries"
import {
  CreateSubscriptionPayload,
  LoanOfficer,
  SubscriptionDetails
} from "../../types"
import { normalizePhone } from "../utils"

type OnConflictCallback = (
  field: "email" | "phone",
  conflicting: SubscriptionDetails | undefined
) => void

// An inactive LO whose NMLS/email matched the form's value. Exposed so the
// profile form can offer same-brand reactivation instead of dead-ending.
export interface InactiveLoMatch {
  loid: string
  name: string
  bank: string
  bank_nid?: string
  field: "nmls" | "email"
}

interface FieldValidationCache {
  lastValue: string
  lastBrandId: string
  lastErrors: Partial<CreateSubscriptionPayload>
  timeout: NodeJS.Timeout | null
  pending: Promise<Partial<CreateSubscriptionPayload>> | null
}

const createFieldCache = (): FieldValidationCache => ({
  lastValue: "",
  lastBrandId: "",
  lastErrors: {},
  timeout: null,
  pending: null
})

export const useValidateEmailAndNMLS = () => {
  const [lazyValidateNMLS] = useLazyValidateNMLSQuery()
  const [lazyValidateEmail] = useLazyValidateEmailQuery()
  const [lazyFetchSubscriptionDetailsQuery] =
    useLazyFetchSubscriptionDetailsQuery()
  const [lazyFetchSubscriptionMatchesQuery] =
    useLazyFetchSubscriptionMatchesQuery()
  const [lazyFetchSubscriptionsByLeadId] =
    useLazyFetchSubscriptionsByLeadIdQuery()

  const emailCacheRef = useRef<FieldValidationCache>(createFieldCache())
  const phoneCacheRef = useRef<FieldValidationCache>(createFieldCache())

  const lastValidatedNMLSRef = useRef<string>("")
  const lastValidatedLOEmailRef = useRef<string>("")
  const lastValidatedExcludeIdRef = useRef<string | undefined>("")
  const lastLOErrorsRef = useRef<Partial<LoanOfficer>>({})

  const [inactiveMatch, setInactiveMatch] = useState<InactiveLoMatch | null>(
    null
  )

  const clearInactiveMatch = useCallback(() => setInactiveMatch(null), [])

  const validateSubscriptionField = useCallback(
    (
      field: "email" | "phone",
      normalizedValue: string,
      values: CreateSubscriptionPayload | SubscriptionDetails,
      brandId: string,
      cache: MutableRefObject<FieldValidationCache>,
      leadId?: string,
      context?: "subscription" | "dreamRate",
      onConflict?: OnConflictCallback
    ): Promise<Partial<CreateSubscriptionPayload>> => {
      const c = cache.current

      if (normalizedValue === c.lastValue && brandId === c.lastBrandId) {
        if (c.pending) return c.pending
        return Promise.resolve(c.lastErrors)
      }

      if (c.timeout) {
        clearTimeout(c.timeout)
        c.timeout = null
      }

      const p = new Promise<Partial<CreateSubscriptionPayload>>(
        (resolve, reject) => {
          c.timeout = setTimeout(async () => {
            try {
              const errors: Partial<CreateSubscriptionPayload> = {}

              c.lastValue = normalizedValue
              c.lastBrandId = brandId

              if (normalizedValue) {
                const queryArg = { [field]: normalizedValue, brand_id: brandId }
                const { data, status } =
                  field === "phone"
                    ? await lazyFetchSubscriptionMatchesQuery(queryArg)
                    : await lazyFetchSubscriptionDetailsQuery(queryArg)

                if (status === QueryStatus.fulfilled) {
                  // Returns true when the found subscription belongs to the current lead.
                  // When lead_id is "0" the API has no linkage stored; in create-from-lead
                  // mode (leadId present, no existing subscription id on values) we treat
                  // unlinked subscriptions as non-conflicting. For other contexts we fall
                  // back to comparing subscription ids via the lead's known subscriptions.
                  const isCurrentLeadSubscription = async (
                    matchLeadId: string | undefined,
                    matchId: string | undefined
                  ): Promise<boolean> => {
                    if (matchLeadId && matchLeadId !== "0")
                      return matchLeadId === (values.lead_id ?? leadId)
                    // lead_id is "0" — unlinked subscription
                    // create-from-lead: unlinked subscriptions are non-conflicting
                    if (leadId && !(values as any).id) return true
                    // edit mode: compare by subscription id (same sub = no conflict)
                    const valuesId = (values as any).id
                    if (valuesId && matchId) return matchId === valuesId
                    if (!leadId || !matchId) return false
                    const { data: leadSubs } =
                      await lazyFetchSubscriptionsByLeadId({
                        lead_id: leadId
                      })
                    return leadSubs?.some(s => s.id === matchId) ?? false
                  }

                  let conflicting: SubscriptionDetails | undefined

                  if (field === "phone" && Array.isArray(data)) {
                    const phoneMatch = data.find(
                      d =>
                        (d as any)?.phone &&
                        normalizePhone(String((d as any).phone)) ===
                          normalizedValue
                    )
                    if (
                      phoneMatch &&
                      !(await isCurrentLeadSubscription(
                        (phoneMatch as any)?.lead_id,
                        (phoneMatch as any)?.id
                      ))
                    )
                      conflicting = phoneMatch as SubscriptionDetails
                  } else if (field === "email" && data) {
                    if (
                      !(await isCurrentLeadSubscription(
                        (data as any)?.lead_id,
                        (data as any)?.id
                      ))
                    )
                      conflicting = data as SubscriptionDetails
                  }

                  if (conflicting) {
                    onConflict?.(field, conflicting)
                    errors[field] =
                      context === "dreamRate"
                        ? `Lead already in Subscriptions. Set its status to Delete.`
                        : `This ${field} is already in use by ${conflicting.first_name} ${conflicting.last_name}`
                    c.lastErrors = errors
                    resolve(errors)
                    return
                  }

                  onConflict?.(field, undefined)
                }
              }

              c.lastErrors = errors
              resolve(errors)
            } catch (error) {
              // If validation fails, resolve with empty errors to not block form
              c.lastErrors = {}
              resolve({})
            } finally {
              c.pending = null
              c.timeout = null
            }
          }, 500)
        }
      )
      c.pending = p
      return p
    },
    [
      lazyFetchSubscriptionDetailsQuery,
      lazyFetchSubscriptionMatchesQuery,
      lazyFetchSubscriptionsByLeadId
    ]
  )

  const validateLO = useCallback(
    async (values: LoanOfficer) => {
      const excludeId = values.uid || values.loid
      const isNotActiveError =
        "This profile is created but inactive. Email support@bankingbridge.com to activate the account."

      const nmlsValue = values.nmls?.trim() || ""
      const emailValue = values.email?.trim() || ""

      const nmlsChanged =
        nmlsValue !== lastValidatedNMLSRef.current ||
        excludeId !== lastValidatedExcludeIdRef.current
      const emailChanged =
        emailValue !== lastValidatedLOEmailRef.current ||
        excludeId !== lastValidatedExcludeIdRef.current

      if (!nmlsChanged && !emailChanged) {
        return lastLOErrorsRef.current
      }

      const errors: Partial<LoanOfficer> = { ...lastLOErrorsRef.current }

      if (nmlsValue && nmlsChanged) {
        const { data, status } = await lazyValidateNMLS({
          nmls: nmlsValue,
          excludeId
        })

        lastValidatedNMLSRef.current = nmlsValue
        lastValidatedExcludeIdRef.current = excludeId

        const hasConflict =
          status === QueryStatus.fulfilled && data?.conflictingLo

        if (!hasConflict) {
          delete errors.nmls
          setInactiveMatch(prev => (prev?.field === "nmls" ? null : prev))
        } else if (!data?.conflictingLo?.active) {
          errors.nmls = isNotActiveError
          const lo = data?.conflictingLo
          if (lo) {
            setInactiveMatch({
              loid: String(lo.loid ?? lo.uid),
              name: lo.name,
              bank: lo.bank,
              bank_nid: lo.bank_nid,
              field: "nmls"
            })
          }
        } else if (
          data?.conflictingLo.loid !== values.uid &&
          data?.conflictingLo.loid !== values.loid
        ) {
          errors.nmls = `This NMLS is already in use by ${data?.conflictingLo.name} (${data?.conflictingLo.bank})`
          setInactiveMatch(prev => (prev?.field === "nmls" ? null : prev))
        } else {
          delete errors.nmls
          setInactiveMatch(prev => (prev?.field === "nmls" ? null : prev))
        }
      } else if (!nmlsValue) {
        delete errors.nmls
      }

      if (emailValue && emailChanged) {
        const { data, status } = await lazyValidateEmail({
          email: emailValue,
          excludeId
        })

        lastValidatedLOEmailRef.current = emailValue
        lastValidatedExcludeIdRef.current = excludeId

        const hasConflict =
          status === QueryStatus.fulfilled && data?.conflictingLo

        if (!hasConflict) {
          delete errors.email
          setInactiveMatch(prev => (prev?.field === "email" ? null : prev))
        } else if (!data?.conflictingLo?.active) {
          errors.email = isNotActiveError
          const lo = data?.conflictingLo
          if (lo) {
            setInactiveMatch({
              loid: String(lo.loid ?? lo.uid),
              name: lo.name,
              bank: lo.bank,
              bank_nid: lo.bank_nid,
              field: "email"
            })
          }
        } else if (
          data?.conflictingLo.uid !== values.uid &&
          data?.conflictingLo.uid !== values.loid
        ) {
          errors.email = `This email is already in use by ${data?.conflictingLo.name} (${data?.conflictingLo.bank})`
          setInactiveMatch(prev => (prev?.field === "email" ? null : prev))
        } else {
          delete errors.email
          setInactiveMatch(prev => (prev?.field === "email" ? null : prev))
        }
      } else if (!emailValue) {
        delete errors.email
      }

      lastLOErrorsRef.current = errors
      return errors
    },
    [lazyValidateNMLS, lazyValidateEmail]
  )

  const validateSubscriptionEmail = useCallback(
    (
      values: CreateSubscriptionPayload | SubscriptionDetails,
      brand_id: string,
      leadId?: string,
      context?: "subscription" | "dreamRate",
      onConflict?: OnConflictCallback
    ) =>
      validateSubscriptionField(
        "email",
        values.email?.trim() || "",
        values,
        brand_id,
        emailCacheRef,
        leadId,
        context,
        onConflict
      ),
    [validateSubscriptionField]
  )

  const validateSubscriptionPhone = useCallback(
    (
      values: CreateSubscriptionPayload | SubscriptionDetails,
      brand_id: string,
      leadId?: string,
      context?: "subscription" | "dreamRate",
      onConflict?: OnConflictCallback
    ) =>
      validateSubscriptionField(
        "phone",
        normalizePhone(values.phone ?? ""),
        values,
        brand_id,
        phoneCacheRef,
        leadId,
        context,
        onConflict
      ),
    [validateSubscriptionField]
  )

  const resetSubscriptionCache = useCallback(() => {
    emailCacheRef.current.lastValue = ""
    emailCacheRef.current.lastBrandId = ""
    emailCacheRef.current.lastErrors = {}
    phoneCacheRef.current.lastValue = ""
    phoneCacheRef.current.lastBrandId = ""
    phoneCacheRef.current.lastErrors = {}
  }, [])

  return {
    validateLO,
    validateSubscriptionEmail,
    validateSubscriptionPhone,
    resetSubscriptionCache,
    inactiveMatch,
    clearInactiveMatch
  }
}
